import clsx from 'clsx';
import type { FunctionComponent, HTMLAttributes, ReactNode } from 'react';

export type BadgeProps = HTMLAttributes<HTMLSpanElement> & {
    readonly children: ReactNode;
    readonly className?: string;
    readonly disabled?: boolean;
    readonly iconOnly?: boolean;
    readonly onClick?: (e: MouseEvent) => void;
    readonly size?: 'm' | 'sm';
    readonly variant?: 'active-card' | 'error' | 'gray' | 'info' | 'orange' | 'success' | 'warning';
};

const baseClasses =
    'inline-flex flex-row items-center justify-center font-sans font-medium transition-colors duration-200 ease select-none whitespace-nowrap border';

const sizeClasses = {
    m: {
        iconOnly: 'h-[28px] w-[28px] gap-0 text-sm leading-none',
        normal: 'h-[28px] px-l gap-xs text-sm leading-none',
    },
    sm: {
        iconOnly: 'h-[24px] w-[24px] gap-0 text-xxs',
        normal: 'h-[24px] px-m gap-xs text-xxs',
    },
} as const;

const variantBaseClasses: Record<NonNullable<BadgeProps['variant']>, string> = {
    'active-card': 'bg-orange-600 border-orange-500 text-orange-50',
    error: 'bg-error-25 border-error-200 text-error-700',
    gray: 'bg-gray-100 border-gray-300 text-gray-800',
    info: 'bg-info-25 border-info-100 text-info-300',
    orange: 'bg-orange-50 border-orange-200 text-orange-800',
    success: 'bg-success-25 border-success-100 text-success-600',
    warning: 'bg-warning-50 border-warning-200 text-warning-800',
};

const variantHoverClasses: Record<NonNullable<BadgeProps['variant']>, string> = {
    'active-card': 'hover:bg-orange-700 hover:border-orange-400 hover:text-orange-50 hover:scale-[1.01]',
    error: 'hover:bg-error-100 hover:border-error-500 hover:text-error-800 hover:scale-[1.01]',
    gray: 'hover:bg-gray-200',
    info: 'hover:bg-info-50',
    orange: 'hover:bg-orange-50 hover:border-orange-500 hover:text-orange-900 hover:scale-[1.01]',
    success: 'hover:bg-success-50',
    warning: 'hover:bg-warning-100',
};

const variantActiveClasses: Record<NonNullable<BadgeProps['variant']>, string> = {
    'active-card': 'active:bg-orange-800 active:border-orange-300 active:text-orange-50 active:scale-[0.98]',
    error: 'active:bg-error-50 active:border-error-700 active:text-error-800 active:scale-[0.98]',
    gray: 'active:bg-gray-200 active:border-gray-800 active:text-gray-900 active:scale-[0.98]',
    info: 'active:bg-info-50 active:border-info-700 active:text-info-800 active:scale-[0.98]',
    orange: 'active:bg-orange-100 active:border-orange-700 active:text-orange-900 active:scale-[0.98]',
    success: 'active:bg-success-50 active:border-success-700 active:text-success-800 active:scale-[0.98]',
    warning: 'active:bg-warning-50 active:border-warning-700 active:text-warning-900 active:scale-[0.98]',
};

export const Badge: FunctionComponent<BadgeProps> = ({
    children,
    className = '',
    disabled = false,
    iconOnly = false,
    onClick,
    size = 'm',
    variant = 'gray',
    ...props
}) => {
    const isClickable = onClick !== undefined;

    return (
        // biome-ignore lint/a11y/noStaticElementInteractions: suppressed due to migration
        // biome-ignore lint/a11y/useKeyWithClickEvents: suppressed due to migration
        <span
            className={clsx(
                baseClasses,
                iconOnly ? 'rounded-[50%]' : 'rounded-full',
                sizeClasses[size][iconOnly ? 'iconOnly' : 'normal'],
                variantBaseClasses[variant],
                disabled && 'opacity-50',
                isClickable && !disabled && variantHoverClasses[variant],
                isClickable && !disabled && variantActiveClasses[variant],
                isClickable && !disabled ? 'cursor-pointer' : 'cursor-default',
                className,
            )}
            onClick={onClick}
            {...props}
        >
            {children}
        </span>
    );
};
